import { OrderStatusEnum } from '@hooks/order';
import { OrderConfirmedIcon } from '@icons/OrderConfirmed';
import { OrderCheckedOutIcon } from '@icons/OrderCheckedOut';
import { OrderDispatchedIcon } from '@icons/OrderDispatched';
import { OrderDeliveringIcon } from '@icons/OrderDelivering';
import { OrderPendingCancellationIcon } from '@icons/OrderPendingCancellation';
import { OrderDeliveringUnsuccessfulIcon } from '@icons/OrderDeliveringUnsuccessful';
import { OrderCompletedIcon } from '@icons/OrderCompleted';
import { OrderCanceledIcon } from '@icons/OrderCanceled';
import { OrderDraftIcon } from '@icons/OrderDraft';
import { RevertCancellationIcon } from '@icons/RevertCancellation';
import { NotificationDetailsFragment } from '@hooks/notification';
import { PromotionIcon } from '@icons/Promotion';
import { TFunction } from 'i18next';

export enum NotificationTypeEnum {
  UserOrderStatus = 'UserNotifications::OrderStatusNotification',
  UserRevertCancellation = 'UserNotifications::RevertCancellationNotification',
  UserPromotionAlert = 'UserNotifications::PromotionNotification',
}

export type Notification = {
  id: string;
  type: NotificationTypeEnum;
  params: any;
  title: string;
  message: string;
  createdAt: string;
  readAt: string;
};

const getStatus = (notification: Notification, t: TFunction) => {
  const orderStatus = notification.params.order_status || notification.params.current_status;
  switch (orderStatus) {
    case OrderStatusEnum.CheckedOut:
      return t('notification.have_been_created');
    case OrderStatusEnum.Confirmed:
      return t('notification.has_been_confirmed');
    case OrderStatusEnum.Dispatched:
      return t('notification.has_been_released_from_stock_for_shipping');
    case OrderStatusEnum.Delivering:
      return t('notification.being_shipped');
    case OrderStatusEnum.PendingCancellation:
      return t('notification.waiting_for_confirmation_to_cancel');
    case OrderStatusEnum.DeliveryUnsuccessful:
      return t('notification.shipping_failure');
    case OrderStatusEnum.Completed:
      return t('notification.has_been_delivered_successfully');
    case OrderStatusEnum.Canceled:
      return t('notification.has_been_cancelled');
    case OrderStatusEnum.Draft:
      return t('notification.has_been_saved');
  }
};

const renderOrderStatusTitle = (notification: Notification, t: TFunction) => {
  const orderStatus = getStatus(notification, t);
  return `${t('notification.order')} ${orderStatus}`;
};

const renderRevertCancellationTitle = (notification: Notification, t: TFunction) => {
  const prevOrderStatus = notification.params.prev_status;
  return prevOrderStatus === OrderStatusEnum.PendingCancellation
    ? t('notification.order_cancellation_request_has_been_denied')
    : t('notification.canceled_order_has_been_reinstated');
};

const renderPromotionAlertTitle = (notification: Notification) => {
  return notification.title;
};

export const renderTitle = (notification: Notification, t: TFunction) => {
  const notificationType = notification.type;
  if (notificationType === NotificationTypeEnum.UserOrderStatus) return renderOrderStatusTitle(notification, t);
  if (notificationType === NotificationTypeEnum.UserRevertCancellation)
    return renderRevertCancellationTitle(notification, t);
  if (notificationType === NotificationTypeEnum.UserPromotionAlert) return renderPromotionAlertTitle(notification);
  return t('notification.new_notification');
};

const renderOrderStatusContent = (notification: Notification, t: TFunction) => {
  const orderId = notification.params.order_id;
  const orderStatus = getStatus(notification, t);
  return (
    <p>
      {t('notification.change_order_status', {
        orderId: orderId,
        orderStatus: orderStatus,
        note: notification.params.order_status === OrderStatusEnum.Draft ? ' và không được thực hiện' : '',
      })}
    </p>
  );
};

const renderRevertCancellationContent = (notification: Notification, t: TFunction) => {
  const orderStatus = getStatus(notification, t);
  const orderId = notification.params.order_id;
  const reasons = notification.params.reasons;
  const prevOrderStatus = notification.params.prev_status;

  const primaryInfo =
    prevOrderStatus === OrderStatusEnum.PendingCancellation ? (
      <span>
        Yêu cầu huỷ đơn hàng số <strong>{orderId}</strong> của bạn đã bị từ chối và đơn hàng đã được chuyển trạng thái
        sang <strong>{orderStatus}</strong>
      </span>
    ) : (
      <span>
        Đơn hàng số <strong>{orderId}</strong> đã được khôi phục và chuyển trạng thái từ <strong>đã được huỷ </strong>
        sang
        <strong> {orderStatus}</strong>
      </span>
    );
  const reasonsInfo = (
    <i>
      Lí do: <strong>{reasons}</strong>
    </i>
  );

  return (
    <>
      <p>{primaryInfo}</p>
      <p>{reasonsInfo}</p>
    </>
  );
};

const renderPromotionAlertContent = (notification: Notification) => {
  return notification.message;
};

export const renderContent = (notification: Notification, t: TFunction) => {
  const notificationType = notification.type;
  if (notificationType === NotificationTypeEnum.UserOrderStatus) return renderOrderStatusContent(notification, t);
  if (notificationType === NotificationTypeEnum.UserRevertCancellation)
    return renderRevertCancellationContent(notification, t);
  if (notificationType === NotificationTypeEnum.UserPromotionAlert) return renderPromotionAlertContent(notification);
  return '';
};

const renderNotificationIcon = (notification: Notification) => {
  const notificationType = notification.type;
  if (notificationType === NotificationTypeEnum.UserRevertCancellation) return RevertCancellationIcon;
  if (notificationType === NotificationTypeEnum.UserPromotionAlert) return PromotionIcon;

  const orderStatus = notification.params.order_status;
  switch (orderStatus) {
    case OrderStatusEnum.CheckedOut:
      return OrderCheckedOutIcon;
    case OrderStatusEnum.Confirmed:
      return OrderConfirmedIcon;
    case OrderStatusEnum.Dispatched:
      return OrderDispatchedIcon;
    case OrderStatusEnum.Delivering:
      return OrderDeliveringIcon;
    case OrderStatusEnum.PendingCancellation:
      return OrderPendingCancellationIcon;
    case OrderStatusEnum.DeliveryUnsuccessful:
      return OrderDeliveringUnsuccessfulIcon;
    case OrderStatusEnum.Completed:
      return OrderCompletedIcon;
    case OrderStatusEnum.Canceled:
      return OrderCanceledIcon;
    case OrderStatusEnum.Draft:
      return OrderDraftIcon;
    default:
      return 'div';
  }
};

const checkIfUserNotification = (notification: Notification) =>
  notification &&
  (notification.type === NotificationTypeEnum.UserOrderStatus ||
    notification.type === NotificationTypeEnum.UserRevertCancellation ||
    notification.type === NotificationTypeEnum.UserPromotionAlert);

export const buildUserNotifications = (notifications: Notification[] = [], t: TFunction) =>
  notifications.filter(checkIfUserNotification).map(notification => ({
    ...notification,
    read: !!notification.readAt,
    title: renderTitle(notification, t),
    content: renderContent(notification, t),
    icon: renderNotificationIcon(notification),
  }));

const buildUserNotificationRedirectUrl = (notification: Notification) => {
  if (notification.type === NotificationTypeEnum.UserPromotionAlert) return `/any/account/campaign`;
  const orderId = notification.params.order_id;
  return `/any/account/order-history/${orderId}`;
};

export const buildRedirectUrl = (notification: Notification) => {
  const isUserNotification = checkIfUserNotification(notification);
  if (isUserNotification) return buildUserNotificationRedirectUrl(notification);
  return '/any';
};

export const isPromotionNotificationOfToday = (notification: NotificationDetailsFragment) =>
  notification.type === NotificationTypeEnum.UserPromotionAlert &&
  new Date(notification.createdAt).getDate() === new Date().getDate();

export const getPromotionNotificationOfToday = (notifications: NotificationDetailsFragment[]) =>
  notifications.filter(notification => isPromotionNotificationOfToday(notification));
